import { Badge, Button, Card, Group, Table, Text, Tooltip } from '@mantine/core';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { Output } from '@/api/analyses';
import type { Report } from '@/api/reports';
import type { GrantedObject, ViewerLink } from '@/api/viewer';
import { ButtonLink } from '@/components/links';
import { rowClick, rowStyle } from '@/components/rowClick';
import { useFormat } from '@/components/useFormat';
import { ExportButton } from '@/features/reports/ExportButton';
import { useKindLabel } from '@/features/objects/useKindLabel';
import { brand } from '@/theme/theme';

export interface OutputsTableProps {
  outputs: Output[];
  /** Staff see digests, flags and links to the object page; clients get the viewer and the export. */
  staff?: boolean;
  projectId?: string;
  /** The project's live reports, so an output that is published links to its page. */
  reports?: Report[];
  /** Whether the analysis has been handed over: nothing is viewable or exportable before that. */
  delivered?: boolean;
  /** What a viewer token granted; an output read through a bucket link can be exported. */
  granted?: GrantedObject[];
  fetchLink?: (object: string, path: string) => Promise<ViewerLink>;
}

export function OutputsTable({ outputs, staff = false, projectId, reports, delivered = false, granted, fetchLink }: OutputsTableProps) {
  const { t } = useTranslation('analyses');
  const { t: tr } = useTranslation('reports');
  const { bytes } = useFormat();
  const kindLabel = useKindLabel();
  const navigate = useNavigate();
  const reportOf = (objectId: string) =>
    (reports ?? []).find((r) => !r.render_state.gone && r.inputs.some((i) => i.object.public_id === objectId));
  if (outputs.length === 0) {
    return (
      <Card>
        <Text fz={14} c={brand.textMuted}>
          {t('outputs.none')}
        </Text>
      </Card>
    );
  }
  return (
    <Card p={0}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('outputs.kind')}</Table.Th>
            <Table.Th>{t('outputs.file')}</Table.Th>
            <Table.Th>{t('outputs.size')}</Table.Th>
            {staff && <Table.Th>digest</Table.Th>}
            {staff && <Table.Th>{t('outputs.deliverable')}</Table.Th>}
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {outputs.map((o) => (
            <Table.Tr
              key={o.public_id}
              style={{ ...(staff ? rowStyle : {}), ...(o.deleted ? { opacity: 0.6 } : {}) }}
              onClick={staff ? rowClick(() => void navigate({ to: '/staff/objects/$objectId', params: { objectId: o.public_id } })) : undefined}
            >
              <Table.Td>
                {staff ? (
                  <Link to="/staff/objects/$objectId" params={{ objectId: o.public_id }} style={{ color: brand.text, fontWeight: 600 }}>
                    {kindLabel(o.kind)}
                  </Link>
                ) : (
                  <Text fw={600}>{kindLabel(o.kind)}</Text>
                )}
              </Table.Td>
              <Table.Td>{o.name || o.declared_name || '—'}</Table.Td>
              <Table.Td>{bytes(o.total_bytes ?? 0)}</Table.Td>
              {staff && (
                <Table.Td>
                  <Text ff="monospace" fz={11}>
                    {o.digest ?? '—'}
                  </Text>
                </Table.Td>
              )}
              {staff && (
                <Table.Td>
                  <Badge size="xs" variant="light" color={o.is_deliverable ? 'forest' : 'gray'}>
                    {o.is_deliverable ? t('outputs.yes') : t('outputs.no')}
                  </Badge>
                </Table.Td>
              )}
              <Table.Td>
                {!staff && delivered && !o.deleted && (
                  <Group gap="xs" justify="flex-end" wrap="nowrap">
                    {(() => {
                      const report = projectId ? reportOf(o.public_id) : undefined;
                      if (report && projectId) {
                        return (
                          <ButtonLink
                            size="xs"
                            variant="default"
                            to="/projects/$projectId/reports/$reportId"
                            params={{ projectId, reportId: report.public_id }}
                          >
                            {t('outputs.view')}
                          </ButtonLink>
                        );
                      }
                      return (
                        <Tooltip label={tr('outputs.noReport')}>
                          <span>
                            <Button size="xs" variant="default" disabled>
                              {t('outputs.view')}
                            </Button>
                          </span>
                        </Tooltip>
                      );
                    })()}
                    {(() => {
                      const grant = o.is_deliverable ? granted?.find((g) => g.object === o.public_id && g.via === 'link') : undefined;
                      return grant?.path && fetchLink ? <ExportButton objectId={o.public_id} path={grant.path} fetchLink={fetchLink} /> : null;
                    })()}
                  </Group>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}
